import { queryOptions } from '@tanstack/react-query'
import type { CustomerTicketPageData } from './easeedeskCustomerTicket'

/**
 * Linkurile biroului — `/api/v2/public/desk/*`, fără sesiune (ca `easeedeskCustomerTicket.ts`).
 */

export class TicketLinkGone extends Error {
  constructor(message = 'This link is no longer valid.') {
    super(message)
    this.name = 'TicketLinkGone'
  }
}

/** Un raspuns refuzat de server, cu codul lui (`state_changed`, `self_approval`...). */
export class DeskLinkError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string) {
    super('Request failed.')
    this.name = 'DeskLinkError'
    this.status = status
    this.code = code
  }
}

async function refused(response: Response): Promise<DeskLinkError> {
  let code = ''
  try {
    const body = (await response.json()) as { error?: unknown }
    code = typeof body.error === 'string' ? body.error : ''
  } catch {
    code = ''
  }
  return new DeskLinkError(response.status, code)
}

export interface DeskAnswerPage {
  ticket: number
  answer: 'solved' | 'still_broken'
  question: string
  labels: Record<string, string>
  lang: string
}

export interface DeskAnswerResult {
  ok: true
  ticket: number
  message: string
  labels: Record<string, string>
}

async function readJson<T>(response: Response): Promise<T> {
  if (response.status === 410) throw new TicketLinkGone()
  if (!response.ok) throw await refused(response)
  return (await response.json()) as T
}

export async function fetchAnswer(token: string, answer: string): Promise<DeskAnswerPage> {
  const response = await fetch(
    `/api/v2/public/desk/answer/${encodeURIComponent(token)}/${encodeURIComponent(answer)}`,
    { headers: { Accept: 'application/json' } },
  )
  return readJson(response)
}

export async function sendAnswer(token: string, answer: string): Promise<DeskAnswerResult> {
  const response = await fetch(
    `/api/v2/public/desk/answer/${encodeURIComponent(token)}/${encodeURIComponent(answer)}`,
    { method: 'POST', headers: { Accept: 'application/json' } },
  )
  return readJson(response)
}

export async function fetchDeskTicket(token: string): Promise<DeskTicketPageData> {
  const response = await fetch(`/api/v2/public/desk/ticket/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  })
  return readJson(response)
}

export interface DeskThreadLine {
  id: number
  by: string
  kind: string
  text: string
  created: string
}

export interface DeskTicketPageData extends CustomerTicketPageData {
  thread?: DeskThreadLine[]
  pending?: { state: string; question?: string }
  labels?: Record<string, string>
  lang?: string
  links?: { human?: string; solved?: string; still_broken?: string; rating?: string }
}

export function deskTicketQuery(token: string) {
  return queryOptions({
    queryKey: ['desk', 'ticket', token],
    queryFn: () => fetchDeskTicket(token) as Promise<DeskTicketPageData>,
    retry: false,
  })
}

export async function replyDeskTicket(token: string, text: string, files: File[]): Promise<{ ok: boolean }> {
  const body = new FormData()
  body.set('text', text)
  files.forEach((f) => body.append('files[]', f))
  const response = await fetch(`/api/v2/public/desk/ticket/${encodeURIComponent(token)}/reply`, { method: 'POST', body })
  if (response.status === 410) throw new TicketLinkGone()
  if (!response.ok) throw new Error('Reply failed.')
  return (await response.json()) as { ok: boolean }
}

export async function askHumanDesk(token: string): Promise<{ ok: boolean; handed: boolean }> {
  const response = await fetch(`/api/v2/public/desk/ticket/${encodeURIComponent(token)}/human`, { method: 'POST', headers: { Accept: 'application/json' } })
  if (response.status === 410) throw new TicketLinkGone()
  if (!response.ok) throw new Error('Request failed.')
  return (await response.json()) as { ok: boolean; handed: boolean }
}

export interface DeskDecisionPage {
  ticket: number
  question: string
  labels: Record<string, string>
  lang: string
  expires_at?: string
  action?: { id: number }
  approver?: { email: string }
  /** Numai pe linkul on-call-ului pentru o ciorna de schimbare a biroului (F8). */
  draft_id?: number
  cab?: { state: 'none' | 'standard' | 'pending' | 'approved' | 'rejected'; ticket_id: number; class: number }
  consent?: { state: string; at: string }
  window?: { start: string; end: string }
  /** Numai pe linkul de aprobare: cum dovedeste aprobatorul ca e el. */
  proof?: DeskApproverProof
}

/**
 * Colegul cu cont decide din sesiunea lui de portal; contactul fara cont, cu
 * codul primit separat pe email. `blocked` = browserul solicitantului.
 */
export type DeskApproverProof =
  | { kind: 'session'; signed_in: boolean; is_approver: boolean; blocked: string | null }
  | { kind: 'code'; code_sent: boolean; sent_to: string; blocked: string | null; error: string | null }

export interface DeskDecisionResult {
  ok: true
  ticket: number
  message: string
}

async function readDecisionJson<T>(response: Response): Promise<T> {
  if (response.status === 410) throw new TicketLinkGone()
  if (!response.ok) throw await refused(response)
  return (await response.json()) as T
}

export async function fetchDeskConfirm(token: string): Promise<DeskDecisionPage> {
  const response = await fetch(`/api/v2/public/desk/confirm/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } })
  return readDecisionJson(response)
}

export async function submitDeskConfirm(token: string, decision: 'yes' | 'no'): Promise<DeskDecisionResult> {
  const body = new URLSearchParams({ decision })
  const response = await fetch(`/api/v2/public/desk/confirm/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  return readDecisionJson(response)
}

export async function fetchDeskApprove(token: string): Promise<DeskDecisionPage> {
  const response = await fetch(`/api/v2/public/desk/approve/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } })
  return readDecisionJson(response)
}

export async function submitDeskApprove(token: string, decision: 'approve' | 'deny', code = ''): Promise<DeskDecisionResult> {
  const body = new URLSearchParams(code !== '' ? { decision, code } : { decision })
  const response = await fetch(`/api/v2/public/desk/approve/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  return readDecisionJson(response)
}

export async function resendDeskApproveCode(token: string): Promise<{ ok: true; code_sent: boolean; sent_to: string }> {
  const response = await fetch(`/api/v2/public/desk/approve/${encodeURIComponent(token)}/code`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  })
  return readDecisionJson(response)
}

export interface DeskConsentPage {
  ticket: { id: number; subject: string }
  change: {
    draft_id: number
    ticket_id: number
    title: string
    machines: number
    window_start: string
    window_end: string
    risk: number
  }
  requester: { name: string }
  lang: string
  expires_at: string
  labels: Record<string, string>
}

export async function fetchDeskConsent(token: string): Promise<DeskConsentPage> {
  const response = await fetch(`/api/v2/public/desk/consent/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } })
  return readDecisionJson(response)
}

export async function submitDeskConsent(token: string, answer: 'agree' | 'decline', note = ''): Promise<DeskDecisionResult> {
  const body = new URLSearchParams({ answer, note })
  const response = await fetch(`/api/v2/public/desk/consent/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  return readDecisionJson(response)
}
